import type { CoachingVisibility, GoalVisibility, Player } from "@/lib/types";

/**
 * One vocabulary for "who sees this", shared by every composer.
 *
 * Before this module the same control shipped four different label
 * sets, four orderings, and three defaults: the note composer said
 * "Coach-only (private)", quick feedback said "Coach-only", the goal
 * composer said "Coaching staff only", and the bulk bar reordered the
 * lot. Mis-sharing is the scariest failure mode a youth coach has, so
 * a control that renames itself between surfaces converts a safety
 * feature into anxiety. Labels, order, and helper text now come from
 * here.
 *
 * The ladder is three values. `unlisted` used to be a fourth, promising
 * "direct link only, not in the team feed" — but nothing implemented the
 * second half: every read path in `services/visibility.py` tested it in
 * the same set as `team`, so it behaved identically and the label was a
 * lie. Retired in migration 0054; the API now rejects it.
 */

export const VISIBILITY_LABELS: Record<CoachingVisibility, string> = {
  private: "Coaches only",
  team: "Whole team",
  player: "This player + family",
};

/**
 * Compact labels for the segmented control, where all three sit side by
 * side in a half-width grid cell inside a height-capped drawer. The full
 * labels wrap to a second row there, and the note/clip composers' drawer
 * is already pinned at `78vh` — the extra height pushes it over the tab
 * strip behind it, which is non-modal and meant to stay clickable.
 *
 * These are a SHORTENING, not a second vocabulary: same order, same
 * meanings, same head noun. The full phrasing still reaches the coach in
 * the same view via `sharedWithSentence` next to Save and
 * `VISIBILITY_DESCRIPTIONS` under the control, so nothing about who
 * receives the item is only available in the short form.
 */
export const VISIBILITY_SHORT_LABELS: Record<CoachingVisibility, string> = {
  private: "Coaches",
  team: "Team",
  player: "Player + family",
};

export const GOAL_VISIBILITY_SHORT_LABELS: Record<GoalVisibility, string> = {
  coach: "Coaches",
  player: "Player + family",
};

/** Option list for the segmented control: compact labels, same order. */
export function visibilitySegmentOptions(
  omit: readonly CoachingVisibility[] = [],
): VisibilityOption[] {
  return visibilityOptions(omit).map((o) => ({
    ...o,
    label: VISIBILITY_SHORT_LABELS[o.value],
  }));
}

export function goalVisibilitySegmentOptions(): Array<{
  value: GoalVisibility;
  label: string;
}> {
  return goalVisibilityOptions().map((o) => ({
    ...o,
    label: GOAL_VISIBILITY_SHORT_LABELS[o.value],
  }));
}

export const VISIBILITY_DESCRIPTIONS: Record<CoachingVisibility, string> = {
  private: "Only the coaching staff. Players and families never see it.",
  team: "Everyone on the team, plus their linked families.",
  player: "Only the players you tag, plus their families.",
};

/** Canonical order: least visible → most visible, so the picker reads
 * as a ladder rather than an arbitrary list. */
export const VISIBILITY_ORDER: readonly CoachingVisibility[] = [
  "private",
  "team",
  "player",
] as const;

export type VisibilityOption = {
  value: CoachingVisibility;
  label: string;
  description: string;
};

/**
 * Narrow a free `string` (from an API payload) to the closed ladder.
 *
 * Rows written before a value was retired still carry it: `unlisted`
 * lived on notes, clips, playlists and recaps until migration 0054, and
 * an app image can serve traffic before that migration lands (a branch
 * preview, a local stack, any environment where the two are deployed
 * separately). Such a value has no matching `<option>`, so the select
 * falls back to its placeholder and — in the composers backed by a zod
 * resolver — blocks submit on a field the coach never touched.
 *
 * Retired values collapse to `team`, which is what they always meant:
 * every read path in `services/visibility.py` tested `unlisted` in the
 * same set as `team`. Anything else unrecognised collapses to
 * `private`, the safest fallback — never widen an audience by guessing.
 *
 * Mirrors `coerceNoteCategory` in Composer.tsx, which protects the
 * sibling `category` field from exactly this.
 */
export function coerceVisibility(
  value: string | null | undefined,
): CoachingVisibility {
  const raw = (value ?? "").trim();
  if ((VISIBILITY_ORDER as readonly string[]).includes(raw)) {
    return raw as CoachingVisibility;
  }
  // Retired in 0054; behaved identically to `team` for its whole life.
  if (raw === "unlisted") return "team";
  return "private";
}

/**
 * Build the option list for a composer.
 *
 * `omit` drops values a surface genuinely cannot deliver — match
 * recaps have no `player_ids` column and
 * `filter_match_summaries_for_user` never surfaces a `player`
 * summary, so offering it there would promise a delivery the backend
 * refuses to make.
 */
export function visibilityOptions(
  omit: readonly CoachingVisibility[] = [],
): VisibilityOption[] {
  const skip = new Set(omit);
  return VISIBILITY_ORDER.filter((v) => !skip.has(v)).map((value) => ({
    value,
    label: VISIBILITY_LABELS[value],
    description: VISIBILITY_DESCRIPTIONS[value],
  }));
}

/** Goals carry their own two-value ladder (`player` | `coach`). Keep
 * the wording identical to the notes ladder so a coach doesn't have
 * to learn "Coaching staff only" as a separate concept. */
export const GOAL_VISIBILITY_LABELS: Record<GoalVisibility, string> = {
  coach: "Coaches only",
  player: "This player + family",
};

export function goalVisibilityOptions(): Array<{
  value: GoalVisibility;
  label: string;
}> {
  return [
    { value: "coach", label: GOAL_VISIBILITY_LABELS.coach },
    { value: "player", label: GOAL_VISIBILITY_LABELS.player },
  ];
}

function playerName(player: Player): string {
  return player.display_name;
}

/**
 * The sentence a composer shows next to Save: exactly who receives
 * this when the coach commits.
 *
 * This is the answer to the question the UI never used to answer, and
 * the reason the help site's top FAQ is "I wrote a note but the player
 * can't see it". `null` means there is no audience at all — the caller
 * should warn rather than reassure.
 */
export function sharedWithSentence(input: {
  visibility: CoachingVisibility | GoalVisibility;
  playerIds?: readonly string[];
  players?: readonly Player[];
}): string | null {
  const { visibility } = input;

  if (visibility === "private" || visibility === "coach") {
    return "Only the coaching staff — nobody else sees this.";
  }

  if (visibility === "team") {
    return "Everyone on the team, their families, and the coaching staff.";
  }

  // `player` visibility delivers to tagged players only. With none
  // tagged it reaches nobody — a valid save that silently goes
  // nowhere, which is the single sharpest trap in the flow.
  const ids = input.playerIds ?? [];
  if (ids.length === 0) return null;

  const byId = new Map((input.players ?? []).map((p) => [p.id, p]));
  const named = ids
    .map((id) => byId.get(id))
    .filter((p): p is Player => !!p)
    .map(playerName);

  // Fall back to a count when the roster hasn't loaded — better a
  // vaguer true sentence than a confident wrong one.
  if (named.length === 0) {
    return `${ids.length} tagged player${ids.length === 1 ? "" : "s"} and their famil${ids.length === 1 ? "y" : "ies"}, plus the coaching staff.`;
  }

  const family = named.length === 1 ? "their family" : "their families";
  return `${formatList(named)} + ${family} + the coaching staff.`;
}

/** The warning shown in place of the sentence when nothing will be
 * delivered. Names the fix, not just the problem. */
export const NO_RECIPIENTS_WARNING =
  "No players tagged — right now this reaches nobody. Tag a player, or switch to Whole team.";

function formatList(names: readonly string[]): string {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  if (names.length <= 4) {
    return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
  }
  const shown = names.slice(0, 3).join(", ");
  return `${shown}, and ${names.length - 3} more`;
}
